'use strict';

var Backbone = require('backbone');
var Radio = require('backbone.radio');
var moment = require('moment-timezone');

var polyglot = require('../utilities/polyglot').default;

var Location = require('../models/location').default;
var Service = require('../models/service').default;

var LocationsCollection = require('../collections/locations').default;
var MarkersCollection = require('../collections/markers').default;
var ServicesCollection = require('../collections/services').default;
var StaffCollection = require('../collections/staff').default;
var TimesCollection = require('../collections/times').default;

var LocationStepView = require('../views/pages/location').default;
var ServiceStepView = require('../views/pages/service').default;
var TimeStepView = require('../views/pages/time').default;
var DetailsStepView = require('../views/pages/details').default;
var PaymentStepView = require('../views/pages/payment').default;
var ConfirmationView = require('../views/pages/confirmation').default;

export default Backbone.Router.extend({

  routes: {
    '': 'default',
  },

  initialize: function(options) {
    this.route(encodeURIComponent(polyglot.t('pages.locations.slug')), 'locationStep');
    this.route(encodeURIComponent(polyglot.t('pages.services.slug')), 'serviceStep');
    this.route(encodeURIComponent(polyglot.t('pages.times.slug')), 'timeStep');
    this.route(encodeURIComponent(polyglot.t('pages.details.slug')), 'detailsStep');
    this.route(encodeURIComponent(polyglot.t('pages.payment.slug')), 'paymentStep');
    this.route(encodeURIComponent(polyglot.t('pages.confirmation.slug')), 'confirmation');

    this.appointment = options.appointment;

    this.settings = window.SETTINGS;
    this.locations = new LocationsCollection(window.LOCATIONS);
    this.markers = new MarkersCollection(window.MARKERS);
    this.services = new ServicesCollection(window.SERVICES);
    this.staff = new StaffCollection();
    this.times = new TimesCollection();

    this.layout = options.layout;

    this.confirmMessage = null;

    // need to check if service step is first and filter locations by the service
    if (this.hideLocationStep()) {
      if (window.LOCATION) {
        this.appointment.setLocation(new Location(window.LOCATION));
      } else {
        this.appointment.setLocation(this.locations.at(0));
      }

      Radio.channel('steps').trigger('remove', polyglot.t('pages.locations.slug'));
    }

    if (window.PREFERRED_LOCATION) {
      this.appointment.setPreferredLocation(new Location(window.PREFERRED_LOCATION));
    }

    if (window.SERVICE) {
      var service = new Service(window.SERVICE);
      this.appointment.addService(service);
      this.locations = this.locations.getByService(service);
      Radio.channel('steps').trigger('remove', polyglot.t('pages.services.slug'));
    }

    if (window.STAFF) {
      var staff = new Backbone.Model(window.STAFF);
      this.appointment.setStaff(staff);
      this.locations = this.locations.getByStaff(staff);
    }

    // if booking appointment from an advanced wait list request
    if (window.REQUEST_START && window.REQUEST) {
      var request = window.REQUEST;
      var start = moment.tz(window.REQUEST_START, request.location.timezone)
        .tz(request.client.timezone);
      window.localStorage.setItem('timezone', request.client.timezone);
      this.appointment.set('start', start);
      this.appointment.set('client', [request.client]);
      window.localStorage.setItem('timezone', request.client.timezone || request.location.timezone);
    }

    this.listenTo(Radio.channel('steps'), 'change:current', this.changeStep);
    this.listenTo(Radio.channel('confirm'), 'confirm:message', this.setConfirmMessage);
  },

  /**
   * Helper method to set the correct message for the confirmation page
   * depending on whether the appointment has been booked or rescheduled
   *
   * @param value
   */
  setConfirmMessage: function(value) {
    this.confirmMessage = value;
  },

  hideLocationStep: function() {
    return !!window.LOCATION || (this.settings.hide_location_step && this.locations.length === 1);
  },

  changeStep: function() {
    this.navigate(Radio.channel('steps').request('current').get('slug'), {trigger: true});
  },

  default: function() {
    this.navigate(Radio.channel('steps').request('current').get('slug'), {trigger: true});
  },

  locationStep: function() {
    if (this.hideLocationStep()) {
      if (Radio.channel('steps').request('first', polyglot.t('pages.locations.slug'))) {
        this.navigate(Radio.channel('steps').request('next').get('slug'), {trigger: true});
        return;
      }
    }

    var preferred_location = this.appointment.getPreferredLocation();

    // only handle these cases when first step is service and preferred_location is set
    if (window.SETTINGS.first_step === 'service' && preferred_location) {
      // if preferred location is set to the same as location, then we are coming back from the
      // time step so just unset the location and send us back to service step
      if (preferred_location.get('id') === this.appointment.get('location_id')) {
        this.appointment.unsetLocation();
        this.navigate(Radio.channel('steps').request('previous').get('slug'), {trigger: true});
        return;
      }

      // if preferred location is set, but there are not services set then lets go back to the
      // service step
      if (!this.appointment.hasServices()) {
        this.navigate(Radio.channel('steps').request('previous').get('slug'), {trigger: true});
        return;
      }

      // if preferred location is set, and has the specified services then we go directly to
      // the time step
      if (preferred_location.hasAllServices(this.appointment.getServices())) {
        this.appointment.setLocation(preferred_location);
        this.navigate(Radio.channel('steps').request('next').get('slug'), {trigger: true});
        return;
      }
    }

    if (!this.previousStepIsValid(polyglot.t('pages.locations.slug'), true)) {
      return;
    }

    Radio.channel('steps').trigger('current', polyglot.t('pages.locations.slug'));

    var view = new LocationStepView({
      appointment: this.appointment,
      markers: this.markers,
      collection: this.locations,
    });

    this.layout.getRegion('main').show(view);
  },

  serviceStep: function() {

    if (!this.previousStepIsValid(polyglot.t('pages.services.slug'), true)) {
      return;
    }

    Radio.channel('steps').trigger('current', polyglot.t('pages.services.slug'));

    var view = new ServiceStepView({
      appointment: this.appointment,
      collection: this.services,
      times: this.times,
      staff: this.staff,
    });

    this.layout.getRegion('main').show(view);
  },

  timeStep: function() {
    if (!this.previousStepIsValid(polyglot.t('pages.times.slug'), true)) {
      return;
    }

    Radio.channel('steps').trigger('current', polyglot.t('pages.times.slug'));

    var view = new TimeStepView({
      appointment: this.appointment,
      collection: this.times,
      staff: this.staff,
      locations: this.locations,
      services: this.services,
    });

    this.layout.getRegion('main').show(view);
  },

  detailsStep: function() {
    if (!this.previousStepIsValid(polyglot.t('pages.details.slug'), true)) {
      return;
    }

    Radio.channel('steps').trigger('current', polyglot.t('pages.details.slug'));

    var view = new DetailsStepView({
      appointment: this.appointment,
    });

    this.layout.getRegion('main').show(view);
  },

  paymentStep: function() {
    if (!this.previousStepIsValid(polyglot.t('pages.payment.slug'), true)) {
      return;
    }

    Radio.channel('steps').trigger('current', polyglot.t('pages.payment.slug'));

    var view = new PaymentStepView({
      appointment: this.appointment,
      services: this.services,
    });

    this.layout.getRegion('main').show(view);
  },

  confirmation: function() {
    if (!window.APPOINTMENTS) {
      this.navigate(Radio.channel('steps').request('start').get('slug'), {trigger: true});
      return;
    }

    var view = new ConfirmationView({
      clientEnd: this.appointment.get('client_end'),
      showMessage: this.confirmMessage,
    });

    Radio.channel('steps').trigger('off');
    this.layout.getRegion('main').show(view);
  },

  previousStepIsValid: function(currentSlug, restart) {
    var previousStep = Radio.channel('steps').request('previous', currentSlug);
    if (previousStep) {
      if (!this.appointment.isValid({step: previousStep.get('slug')})) {
        var destination = restart ? Radio.channel('steps').request('start').get('slug') : previousPage.get('slug');
        this.navigate(destination, {trigger: true});
        return false;
      }
    }
    return true;
  },
});
